import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { posService } from '@/services/pos.service';
import { useSerialTrackingEnabled } from '@/hooks/useGeneralConfig';
import { SEARCH_DEBOUNCE_MS } from '@/constants/searchUi';
import type { FulfillmentMode, PosVariantSearchResult } from '../types';

const EMPTY_LIST: never[] = [];
const EMPTY: PosVariantSearchResult = { serials: [], variants: [], combos: [] };

export function useQrVariantSearch(
  qrCode: string,
  fulfillmentMode: FulfillmentMode,
  // TCR-1126 review round 2 — chỉ màn POS truyền `true` (qua QRSearchBar
  // `posChannelOnly` prop). Mặc định vắng mặt → không gửi `pos_channel_only`,
  // giữ hành vi cũ cho Sửa đơn/Đổi hàng.
  posChannelOnly?: boolean,
) {
  const [debounced, setDebounced] = useState('');

  useEffect(() => {
    const trimmed = qrCode.trim();
    if (!trimmed) {
      setDebounced('');
      return;
    }
    const t = setTimeout(() => setDebounced(trimmed), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [qrCode]);

  const enabled = debounced.length > 0;
  const { enabled: serialTrackingEnabled } = useSerialTrackingEnabled();

  const query = useQuery<PosVariantSearchResult>({
    queryKey: ['pos-variant-search', debounced, fulfillmentMode, posChannelOnly],
    queryFn: () => posService.searchVariant(debounced, fulfillmentMode, posChannelOnly),
    enabled,
    staleTime: 30_000,
    gcTime: 60_000,
    retry: false,
  });

  // BE đã không trả serial khi setting tắt; lọc thêm ở FE để dropdown không
  // bao giờ gợi ý serial ngay cả khi response cũ còn trong cache.
  const raw = query.data ?? EMPTY;
  // BE cũ chưa deploy sẽ không có field `combos` — suy biến thành mảng rỗng
  // thay vì undefined, để nơi render không phải tự phòng thủ.
  const combos = raw.combos ?? EMPTY_LIST;
  const results: PosVariantSearchResult = serialTrackingEnabled
    ? { ...raw, combos }
    : { serials: EMPTY_LIST, variants: raw.variants, combos };

  return {
    results,
    isSearching: enabled && query.isFetching,
    // TCR-2664 — API lỗi phải tách khỏi "không có kết quả" để UI không báo nhầm "không tìm thấy".
    // Chỉ coi là lỗi khi từ khoá đã debounce khớp từ khoá đang gõ (tránh hiện lỗi của từ khoá cũ).
    isError: enabled && debounced === qrCode.trim() && query.isError,
    /** Gọi lại đúng từ khoá đang lỗi (nút "Thử lại"). */
    retry: () => {
      void query.refetch();
    },
    hasResults:
      results.serials.length > 0 || results.variants.length > 0 || results.combos.length > 0,
  };
}
